export interface BlogFrontmatter {
  title: string;
  description: string;
  date: string;
  slug: string;
  tags?: string[];
  draft?: boolean;
  featuredImage?: string;
  imageAlt?: string;
}

export interface BlogPost {
  slug: string;
  meta: BlogFrontmatter;
  body: string;
}

const rawModules = import.meta.glob<string>("../../content/blog/*.md", {
  eager: true,
  query: "?raw",
  import: "default",
});

function pathToSlug(path: string): string {
  const base = path.split("/").pop() || "";
  return base.replace(/\.md$/, "");
}

/** Lightweight frontmatter parser — no Node Buffer dependency. */
function parseFrontmatter(raw: string): { data: Record<string, unknown>; content: string } {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);
  if (!match) return { data: {}, content: raw };

  const yaml = match[1];
  const content = match[2];
  const data: Record<string, unknown> = {};

  let currentKey = "";
  let collectingArray = false;

  for (const line of yaml.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    if (collectingArray && trimmed.startsWith("- ")) {
      const val = trimmed.slice(2).trim().replace(/^["']|["']$/g, "");
      (data[currentKey] as string[]).push(val);
      continue;
    }
    collectingArray = false;

    const kv = trimmed.match(/^(\w[\w\-]*)\s*:\s*(.*)$/);
    if (!kv) continue;

    const key = kv[1];
    let val = kv[2].trim();

    if (val === "" || val === "[]") {
      // Could be start of a YAML array on next lines
      data[key] = [];
      currentKey = key;
      collectingArray = val === "" || val === "[]";
      continue;
    }

    // Remove surrounding quotes
    val = val.replace(/^["']|["']$/g, "");

    if (val === "true") data[key] = true;
    else if (val === "false") data[key] = false;
    else data[key] = val;
  }

  return { data, content };
}

function parseAll(): BlogPost[] {
  const posts: BlogPost[] = [];
  for (const [path, raw] of Object.entries(rawModules)) {
    const { data, content } = parseFrontmatter(raw);
    const d = data as Partial<BlogFrontmatter>;
    const slug = (d.slug as string) || pathToSlug(path);
    if (d.draft) continue;
    posts.push({
      slug,
      meta: {
        title: (d.title as string) || slug,
        description: (d.description as string) || "",
        date: (d.date as string) || "",
        slug,
        tags: d.tags as string[] | undefined,
        draft: d.draft as boolean | undefined,
        featuredImage: d.featuredImage as string | undefined,
        imageAlt: d.imageAlt as string | undefined,
      },
      body: content.trim(),
    });
  }
  return posts.sort((a, b) => (a.meta.date < b.meta.date ? 1 : -1));
}

let cache: BlogPost[] | null = null;

export function getAllPosts(): BlogPost[] {
  if (!cache) cache = parseAll();
  return cache;
}

export function getPostBySlug(slug: string): BlogPost | undefined {
  return getAllPosts().find((p) => p.slug === slug);
}
